import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { ReactNode } from 'react';
import type { WorkbenchSurfaceContext } from '#utils/analytics/analytics-events.js';

interface PageContextEntry {
	label: string;
	context: Record<string, string | undefined>;
	/**
	 * The subset of this entry worth reporting to analytics, under its analytics
	 * property names. Opt-in and typed so nothing reaches analytics merely by
	 * being useful to the agent — the prompt carries design detail that the
	 * events deliberately do not.
	 */
	analytics?: WorkbenchSurfaceContext;
}

interface AgentWorkbenchPageOps {
	add: (handle: object, entry: PageContextEntry) => void;
	remove: (handle: object) => void;
}

/**
 * Stable write-only context. Only contains add/remove — never changes after
 * the provider mounts, so writer components do NOT re-render when state changes.
 */
const agentWorkbenchOpsContext = createContext<AgentWorkbenchPageOps>({ add: () => {}, remove: () => {} });

/** Reactive read context. Only subscribed to by components that display context. */
const agentWorkbenchStateContext = createContext<readonly PageContextEntry[]>([]);

/** Scoped boundary for agent workbench page context — each instance is independent. */
export function AgentWorkbenchPageContextProvider({ children }: { children: ReactNode }) {
	const [entries, setEntries] = useState<ReadonlyMap<object, PageContextEntry>>(() => new Map());

	const ops = useMemo<AgentWorkbenchPageOps>(
		() => ({
			add: (handle, entry) => {
				setEntries((prev) => {
					if (prev.get(handle) === entry) {
						return prev;
					}
					const next = new Map(prev);
					next.set(handle, entry);
					return next;
				});
			},
			remove: (handle) => {
				setEntries((prev) => {
					if (!prev.has(handle)) {
						return prev;
					}
					const next = new Map(prev);
					next.delete(handle);
					return next;
				});
			},
		}),
		[],
	);

	const entriesList = useMemo(() => Array.from(entries.values()), [entries]);

	return (
		<agentWorkbenchOpsContext.Provider value={ops}>
			<agentWorkbenchStateContext.Provider value={entriesList}>{children}</agentWorkbenchStateContext.Provider>
		</agentWorkbenchOpsContext.Provider>
	);
}

/**
 * Registers a labeled context section into the nearest `AgentWorkbenchPageContextProvider`.
 * Removed on unmount; undefined values are omitted from the agent prompt.
 *
 * @param label - Section heading shown to the agent.
 * @param context - Key-value pairs to include.
 * @param analytics - Optional subset to attach to this surface's workbench events.
 */
export function useAddAgentWorkbenchPageContext(
	label: string,
	context: Record<string, string | undefined>,
	analytics?: WorkbenchSurfaceContext,
): void {
	// Subscribe ONLY to the stable ops context — not to state — so this hook
	// never re-renders in response to its own writes (which would loop).
	const { add, remove } = useContext(agentWorkbenchOpsContext);
	const handle = useRef({}).current;
	// Serialized separately: the dependency list must be simple expressions, and
	// both bags are re-created by the caller on every render.
	const contextKey = JSON.stringify(context);
	const analyticsKey = JSON.stringify(analytics);
	const entry = useMemo(
		() => ({ label, context, analytics }),
		[label, contextKey, analyticsKey], // eslint-disable-line react-hooks/exhaustive-deps
	);

	useEffect(() => {
		add(handle, entry);
		return () => remove(handle);
	}, [add, remove, handle, entry]);
}

/** Component form of {@link useAddAgentWorkbenchPageContext} for use in render returns. */
export function AddAgentWorkbenchPageContext({
	label,
	context,
	analytics,
}: {
	label: string;
	context: Record<string, string | undefined>;
	analytics?: WorkbenchSurfaceContext;
}) {
	useAddAgentWorkbenchPageContext(label, context, analytics);
	return null;
}

/**
 * Merges every registered entry's opted-in analytics properties into one bag,
 * for attaching to this surface's workbench events. Later registrations win on a
 * key collision, so a nested route can sharpen what its layout set.
 *
 * @returns The merged properties; empty when nothing opted in.
 */
export function useAgentWorkbenchPageContextAnalytics(): WorkbenchSurfaceContext {
	const entries = useContext(agentWorkbenchStateContext);

	return useMemo(() => {
		const merged: WorkbenchSurfaceContext = {};
		for (const { analytics } of entries) {
			if (analytics === undefined) {
				continue;
			}
			for (const key of Object.keys(analytics) as (keyof WorkbenchSurfaceContext)[]) {
				const value = analytics[key];
				if (value !== undefined) {
					merged[key] = value;
				}
			}
		}
		return merged;
	}, [entries]);
}

/**
 * Returns all registered context entries as a formatted string, or `undefined`
 * when there's nothing to include (so callers can skip sending the context field).
 *
 * @returns Formatted page-context string or `undefined`.
 */
export function useAgentWorkbenchPageContextText(): string | undefined {
	const entries = useContext(agentWorkbenchStateContext);

	return useMemo(() => {
		// Merge by label so a layout + sub-route both contributing "Package Store"
		// produce one section rather than two; later keys overwrite on collision.
		const byLabel = new Map<string, Record<string, string | undefined>>();
		for (const { label, context } of entries) {
			byLabel.set(label, { ...byLabel.get(label), ...context });
		}

		const sections = Array.from(byLabel.entries())
			.map(([label, context]) => {
				const lines = Object.entries(context)
					.filter((pair): pair is [string, string] => pair[1] !== undefined)
					.map(([k, v]) => `- ${k}: ${v}`);
				if (lines.length === 0) {
					return null;
				}
				return `**${label}**\n${lines.join('\n')}`;
			})
			.filter((s): s is string => s !== null);

		if (sections.length === 0) {
			return undefined;
		}
		return `[Current page context]\n\n${sections.join('\n\n')}`;
	}, [entries]);
}
